import { Link } from '@tanstack/react-router';
import { Bell, BellOff } from 'lucide-react';
import type { ReactNode } from 'react';
import { Popover, usePopover } from '../components/Popover';
import { DeepLink } from '../features/notifications/components/DeepLink';
import { useMarkRead, useNotificationBell } from '../features/notifications/api/notifications-api';
import { formatDateTimeLong, formatRelative } from '../lib/format';
import styles from './UtilityBar.module.css';
import panelStyles from './NotificationPanel.module.css';

/**
 * The bell, reading the real inbox from S8.
 *
 * It owns its own query rather than taking rows as props, which is what keeps the
 * shell itself from re-rendering when a notification arrives (CLAUDE.md §5a rule
 * 6): the push invalidates the `['notifications']` key, TanStack Query refetches,
 * and only this component re-renders.
 *
 * Every row is a real anchor to a real record. A notification that lands on a
 * dashboard is a bug (non-negotiable #3), and open-in-new-tab has to work on one
 * (06-qol §8) — which is why these are anchors rather than click handlers even
 * though the click is intercepted for in-app navigation.
 */
export function NotificationBell(): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const inbox = useNotificationBell();
  const markRead = useMarkRead();

  const rows = inbox.data?.rows ?? [];
  const unread = inbox.data?.unreadCount ?? 0;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={styles.iconButton}
        onClick={toggle}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={unread === 0 ? 'Notifications' : `Notifications, ${unread} unread`}
      >
        <Bell size={17} aria-hidden />
        {unread > 0 && (
          <span className={styles.badge} aria-hidden>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      <Popover anchor={anchor} onClose={close} align="right" label="Notifications">
        <div className={panelStyles.panel}>
          <header className={panelStyles.header}>
            <span className={panelStyles.title}>Notifications</span>
            {unread > 0 && (
              <button
                type="button"
                className={panelStyles.markAll}
                onClick={() => markRead.mutate({})}
              >
                Mark all read
              </button>
            )}
          </header>

          {rows.length === 0 ? (
            <div className={panelStyles.list}>
              <div className={panelStyles.item}>
                <BellOff size={16} aria-hidden />
                <div className={panelStyles.itemText}>
                  <span className={panelStyles.itemTitle}>Nothing yet</span>
                  <span className={panelStyles.itemBody}>
                    You will be told here when a scorecard is due, an approval needs you, or an
                    SLA is close to breaching.
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className={panelStyles.list}>
              {rows.map((row) => (
                <DeepLink
                  key={row.id}
                  to={row.deepLinkUrl}
                  className={`${panelStyles.item} ${row.readAt === null ? panelStyles.unread : ''}`}
                  onNavigate={() => {
                    close();
                    if (row.readAt === null) markRead.mutate({ ids: [row.id] });
                  }}
                >
                  <div className={panelStyles.itemText}>
                    <span className={panelStyles.itemTitle}>{row.title}</span>
                    {row.body !== null && (
                      <span className={panelStyles.itemBody}>{row.body}</span>
                    )}
                  </div>
                  <span
                    className={panelStyles.itemTime}
                    title={formatDateTimeLong(row.createdAt)}
                  >
                    {formatRelative(row.createdAt)}
                  </span>
                </DeepLink>
              ))}
            </div>
          )}

          <footer className={panelStyles.footer}>
            <Link to="/notifications" className={panelStyles.markAll} onClick={close}>
              See all notifications
            </Link>
          </footer>
        </div>
      </Popover>
    </>
  );
}
